import { createRouter, createWebHashHistory } from 'vue-router'
import originalRoutes from './routes'
import { api } from '@/service/api'
import {
  redirectToWeChatOAuth,
  wechatSilentLogin,
  wechatUserinfoLogin,
  WX_BIND_CTX_KEY,
} from '@/service/wechat'
import { WX_AUTH_CALLBACK_PATH, WX_OAUTH_PHASE } from '@/config/wechat'
import { isWeChat } from '@/utils'

const authFailurePath = '/auth-fail'

const router = createRouter({
  history: createWebHashHistory(),
  routes: originalRoutes,
})

const getUserinfo = async () => {
  if (window.$store.userInfo.id) return
  const res = await api('user', 'userinfo')
  if (res.code == 200) {
    window.$store.userInfo = res.data
    if (!window.$store.currentProject.id) {
      const cur_project_id = res.data.config?.cur_project_id
      if (!cur_project_id) {
        window.$message?.error('未获取到当前项目')
        return
      }
      await getProject(cur_project_id)
    }
    await Promise.all([
      window.$fetchAlarmLevels?.(),
      window.$fetchProjectStypes?.(),
    ])
  }
}

const getProject = async (id) => {
  if (window.$store.currentProject.id) return
  const res = await api('project', 'getbyid', { id })
  if (res.code == 200) {
    window.$store.currentProject = res.data
  }
}

/** URL 上的 app_token 写入 localStorage */
const syncTokenFromUrl = (to) => {
  const appToken = to.query?.app_token
  if (appToken){
    localStorage.setItem('token', String(appToken))
  }
}

/** URL 上的 project_id / project_name 写入 currentProject */
const syncProjectFromUrl = (to) => {
  const projectId = to.query?.project_id
  const projectName = to.query?.project_name
  if (projectId && projectName) {
    window.$store.currentProject = {
      id: String(projectId),
      name: String(projectName),
    }
  }
}


/** 处理 silent / userinfo 登录结果；返回 true 表示已 next，false 表示未绑定需调用方继续 */
const handleLoginResult = (res, next) => {
  // 403/409：带后端 message 进失败页
  if (res.status === 'fail') {
    next({ path: authFailurePath, query: { msg: res.message }, replace: true })
    return true
  }

  // 400：拦截器已 toast，进通用失败页（可重新授权）
  if (res.status === 'toast') {
    next({ path: authFailurePath, replace: true })
    return true
  }

  if (res.status === 'bound' && res.data?.token) {
    localStorage.setItem('token', res.data.token)
    const projectId = window.$store.currentProject?.id
    next({ path: projectId ? `/${projectId}/alarm/list` : '/', replace: true })
    return true
  }

  if (res.status === 'unbound') {
    return false
  }

  next({ path: authFailurePath, replace: true })
  return true
}

// 无 token → snsapi_base 静默授权
//   → 回调(base) → silent-login(openid 查绑定) → 已绑定? 登录 : 跳转 snsapi_userinfo
//   → 回调(userinfo) → userinfo-login(unionid 再查绑定) → 已绑定? 登录 : /login 绑定页
//   → 用户提交 → bind-login({ openid, unionid, email, password })
const weChatGuard = async (to, next, token) => {
  const searchParams = new URLSearchParams(window.location.search)
  // ========== 1. 微信回调 /wx-callback ==========
  if (new RegExp(`^${WX_AUTH_CALLBACK_PATH}/?$`).test(window.location.pathname)) {
    const code = searchParams.get('code')
    const oauthPhase = searchParams.get('state')
    sessionStorage.removeItem('wx_oauth_ing')

    // 无 code：拒绝授权，或已清 URL 后的二次进入；清掉 /wx-callback 后放行由外层处理
    if (!code || !oauthPhase) {
      window.history.replaceState(
        {},
        document.title,
        `${window.location.origin}/${window.location.hash || ''}`
      )
      return false
    }

    // 清掉 code，并把 /wx-callback 还原为 /，避免业务页 URL 残留回调 path
    window.history.replaceState(
      {},
      document.title,
      `${window.location.origin}/${window.location.hash || ''}`
    )

    // userinfo 阶段：用 unionid 再查绑定（兼容小程序已绑、公众号首次访问）
    if (oauthPhase === WX_OAUTH_PHASE.USERINFO) {
      try {
        const res = await wechatUserinfoLogin(code)
        const handled = handleLoginResult(res, next)
        if (handled) return true

        // 未绑定 → 绑定页
        sessionStorage.setItem(
          WX_BIND_CTX_KEY,
          JSON.stringify({
            openid: res.data.openid,
            unionid: res.data.unionid || '',
          })
        )
        next({ path: '/login', query: { bind: '1' }, replace: true })
        return true
      } catch (e) {
        next({
          path: authFailurePath,
          query: { msg: e?.message || '微信授权失败' },
          replace: true,
        })
        return true
      }
    }

    // base 阶段：静默换取 openid，检查是否已绑定
    if (oauthPhase === WX_OAUTH_PHASE.BASE) {
      try {
        const res = await wechatSilentLogin(code)
        const handled = handleLoginResult(res, next)
        if (handled) return true

        // 未绑定 → 拉起 userinfo
        redirectToWeChatOAuth('snsapi_userinfo', WX_OAUTH_PHASE.USERINFO)
        return true
      } catch (e) {
        next({
          path: authFailurePath,
          query: { msg: e?.message || '微信授权失败' },
          replace: true,
        })
        return true
      }
    }

    // state 非法
    next({ path: authFailurePath, replace: true })
    return true
  }

  // ========== 2. 无 token，发起静默 OAuth ==========
  if (!token) {
    const skipPathList = [authFailurePath, '/login']
    if (!skipPathList.includes(to.path)) {
      const oauthIng = sessionStorage.getItem('wx_oauth_ing')
      if (!oauthIng) {
        // 测试公众号不能走静默授权，走用户信息授权
        // 不要动这句代码
        redirectToWeChatOAuth('snsapi_base', WX_OAUTH_PHASE.BASE)
        return true
      }
    }
  }

  return false
}

router.beforeEach(async (to, from, next) => {
  document.title = to.meta?.title ? to.meta?.title : '告警管理'

  syncTokenFromUrl(to)
  syncProjectFromUrl(to)

  const token = localStorage.getItem('token')
  const authFailurePath = '/auth-fail'

  // 微信内无 token：走 OAuth / 绑定流程
  if (isWeChat() && !token) {
    const handled = await weChatGuard(to, next, token)
    if (handled) return
  }

  // /login 仅作微信绑定页（?bind=1），禁止账号密码登录入口
  if (to.path === '/login' && to.query.bind !== '1') {
    if (token) {
      await getUserinfo()
      const projectId = window.$store.currentProject?.id
      next({ path: projectId ? `/${projectId}/alarm/list` : '/', replace: true })
    } else {
      next({ path: authFailurePath, replace: true })
    }
    return
  }

  if (to.meta.auth) {
    if (token) {
      await getUserinfo()
      if (to.name === 'index') {
        const projectId = window.$store.currentProject?.id
        if (projectId) {
          // 默认进入移动端告警列表
          next({ path: `/${projectId}/alarm/list`, replace: true })
          return
        }
      }
      next()
    } else {
      // 无 token：不能走自身登录，统一到失败页
      next({ path: authFailurePath, replace: true })
    }
  } else if (token && to.path === '/login') {
    // 已登录访问绑定页 → 进业务页
    await getUserinfo()
    const projectId = window.$store.currentProject?.id
    next(projectId ? `/${projectId}/alarm/list` : '/')
  } else {
    next()
  }
})

export default router
